import type { FicSettingsStatus } from "@cem/module-fic/server";
import type { FicSynchronizationStatus } from "@cem/contracts";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, NavLink } from "react-router-dom";
import { api } from "./api.js";
import { TopbarIcon } from "./TopbarIcon.js";

type FicTone = "connected" | "attention" | "disconnected" | "loading";

interface FicConnectionContextValue {
  enabled: boolean;
  status: FicSettingsStatus | null;
  loading: boolean;
  error: string | null;
  synchronization: FicSynchronizationStatus | null;
  refresh: () => Promise<FicSettingsStatus | null>;
}

const FicConnectionContext = createContext<FicConnectionContextValue | null>(
  null,
);

const dateTime = (value: string | null): string =>
  value === null
    ? "Mai"
    : new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));

const toneFor = (
  status: FicSettingsStatus | null,
  loading: boolean,
): FicTone => {
  if (loading && status === null) return "loading";
  if (status?.connectionState === "connected") return "connected";
  if (
    status?.connectionState === "attention" ||
    status?.connectionState === "not-configured"
  )
    return "attention";
  return "disconnected";
};

export const FicConnectionProvider = ({
  children,
  enabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) => {
  const [status, setStatus] = useState<FicSettingsStatus | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [synchronization, setSynchronization] =
    useState<FicSynchronizationStatus | null>(null);

  const refresh = useCallback(async (): Promise<FicSettingsStatus | null> => {
    if (!enabled) {
      setLoading(false);
      return null;
    }
    try {
      const [result, sync] = await Promise.all([
        api.get<FicSettingsStatus>("/api/v1/fic/settings"),
        api.get<FicSynchronizationStatus>("/api/v1/fic/synchronization"),
      ]);
      setStatus(result);
      setSynchronization(sync);
      setError(null);
      return result;
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : "Stato FIC non disponibile";
      setError(message);
      throw cause;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setStatus(null);
      setSynchronization(null);
      setError(null);
      return;
    }
    void refresh().catch(() => undefined);
    const interval = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, 60_000);
    const onFocus = () => void refresh().catch(() => undefined);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, refresh]);

  const value = useMemo(
    () => ({ enabled, status, loading, error, refresh, synchronization }),
    [enabled, error, loading, refresh, status, synchronization],
  );

  return (
    <FicConnectionContext.Provider value={value}>
      {children}
    </FicConnectionContext.Provider>
  );
};

export const useFicConnection = (): FicConnectionContextValue => {
  const value = useContext(FicConnectionContext);
  if (value === null) throw new Error("FicConnectionProvider non disponibile");
  return value;
};

export const FicConnectionIndicator = () => {
  const { enabled, status, loading, error, synchronization } =
    useFicConnection();
  if (!enabled) return null;
  const selectedCompany =
    status?.companies.find(({ selected }) => selected) ?? null;
  const allFresh =
    synchronization !== null &&
    synchronization.resources.length > 0 &&
    synchronization.resources.every(
      (r) => (r.downloadState ?? r.state) === "fresh",
    );
  const pending =
    synchronization?.resources.some((r) => (r.pendingAssociations ?? 0) > 0) ??
    false;
  const warnings =
    synchronization?.resources.some((r) => (r.lastRunWarnings ?? 0) > 0) ??
    false;
  const tone =
    error !== null
      ? "attention"
      : status?.connectionState === "connected" &&
          (!allFresh || pending || warnings)
        ? "attention"
        : toneFor(status, loading);
  const label =
    error !== null
      ? "Stato FIC non verificabile"
      : tone === "connected"
        ? "Dati aggiornati"
        : tone === "loading"
          ? "Verifica in corso"
          : tone === "attention"
            ? status?.connectionState === "connected"
              ? allFresh
                ? pending
                  ? "Lettura aggiornata · associazioni da verificare"
                  : "Lettura aggiornata · segnalazioni"
                : "Collegato · lettura da aggiornare"
              : "Da verificare"
            : "Non collegato";
  const detail =
    selectedCompany?.name ??
    (error === null ? "Apri le impostazioni" : "Stato non disponibile");

  return (
    <NavLink
      aria-label={`Fatture in Cloud: ${label}. ${detail}`}
      title={`Fatture in Cloud: ${label}. ${detail}`}
      className={`fic-global-status topbar-icon-action ${tone}`}
      to="/settings"
    >
      <TopbarIcon name="cloud" />
      <span className="topbar-connection-state" aria-hidden="true">
        {tone === "connected"
          ? "✓"
          : tone === "attention"
            ? "!"
            : tone === "loading"
              ? "…"
              : "×"}
      </span>
    </NavLink>
  );
};

export const FicDiagnosticItem = ({
  label,
  detail,
  state,
}: {
  label: string;
  detail: string;
  state: "done" | "open" | "neutral";
}) => (
  <div className={`fic-diagnostic-item ${state}`}>
    <i aria-hidden="true" />
    <span>
      <strong>{label}</strong>
      <small>{detail}</small>
    </span>
    <b>{state === "done" ? "OK" : state === "open" ? "KO" : "ATTESA"}</b>
  </div>
);

export const FicDashboardConnection = ({
  dataAvailable,
  dataLastSyncAt,
}: {
  dataAvailable: boolean;
  dataLastSyncAt: string | null;
}) => {
  const { status, loading, error, synchronization } = useFicConnection();
  const documents = synchronization?.resources.find(
    (r) => r.resource === "documents",
  );
  const fresh = (documents?.downloadState ?? documents?.state) === "fresh";
  const tone =
    error || (status?.connectionState === "connected" && !fresh)
      ? "attention"
      : toneFor(status, loading);
  const connected = status?.connectionState === "connected";
  const selectedCompany =
    status?.companies.find(({ selected }) => selected) ?? null;
  const missingScopes =
    status?.requestedScopes.filter(
      (scope) => !status.grantedScopes.includes(scope),
    ) ?? [];
  const permissionsComplete =
    status !== null &&
    status.requestedScopes.length > 0 &&
    missingScopes.length === 0;
  const title =
    tone === "loading"
      ? "VERIFICA FIC…"
      : connected
        ? "FIC CONNESSO"
        : "FIC DA VERIFICARE";
  const description =
    connected && error === null
      ? dataAvailable && fresh
        ? `Lettura FIC aggiornata. Documenti ancora da associare: ${documents?.pendingAssociations ?? "da verificare"}.`
        : dataAvailable
          ? "Connessione operativa. La lettura dei documenti deve essere aggiornata."
          : "Connessione operativa. I dati della dashboard attendono la prima sincronizzazione."
      : (error ?? "Completa o verifica il collegamento dalle impostazioni.");

  return (
    <section className={`fic-connection-panel ${tone}`} aria-label="Stato FIC">
      <div className="fic-connection-summary">
        <p className="eyebrow">COLLEGAMENTO ESTERNO</p>
        <div className={`fic-online-status ${tone}`}>
          <i aria-hidden="true" />
          {title}
        </div>
        <p>{description}</p>
        <Link to="/settings">Apri configurazione FIC</Link>
      </div>
      <div className="fic-diagnostics">
        <FicDiagnosticItem
          detail={
            connected
              ? `Verificato ${dateTime(status?.lastVerifiedAt ?? null)}`
              : "Token non operativo"
          }
          label="Autorizzazione OAuth"
          state={loading ? "neutral" : connected ? "done" : "open"}
        />
        <FicDiagnosticItem
          detail={selectedCompany?.name ?? "Nessuna azienda selezionata"}
          label="Azienda operativa"
          state={loading ? "neutral" : selectedCompany ? "done" : "open"}
        />
        <FicDiagnosticItem
          detail={
            status === null
              ? "Permessi non letti"
              : `${String(status.grantedScopes.length)}/${String(status.requestedScopes.length)} permessi di lettura`
          }
          label="Copertura API"
          state={loading ? "neutral" : permissionsComplete ? "done" : "open"}
        />
        <FicDiagnosticItem
          detail={
            dataAvailable
              ? `Ultima acquisizione ${dateTime(dataLastSyncAt)}`
              : "Esegui Aggiorna dati FIC"
          }
          label="Dati dashboard"
          state={dataAvailable && fresh ? "done" : "neutral"}
        />
      </div>
    </section>
  );
};
